/**
 * Navbar deep links: path ↔ App.js currentView ids.
 * Used for typed URLs and (later) email CTAs — not full SPA history routing.
 */

import { DEFAULT_LANDING_VIEW, resolveLandingView } from './landingView';

export const POST_LOGIN_VIEW_KEY = 'post_login_view';

/** Canonical path for each navbar view (1:1 with currentView ids). */
export const VIEW_TO_PATH = {
  dashboard: '/dashboard',
  devices: '/devices',
  'assigned-devices': '/assigned-devices',
  complaints: '/complaints',
  alerts: '/alerts',
  analytics: '/analytics',
  approvals: '/approvals',
  'location-transfers': '/location-transfers',
  'department-transfers': '/department-transfers',
  'maintenance-history': '/maintenance-history',
  'work-logs': '/work-logs',
  'activity-log': '/activity-log',
  users: '/users',
  settings: '/settings',
  'organization-settings': '/organization',
  'audit-viewer': '/audit-viewer',
  'data-import': '/data-import',
  locations: '/locations',
  departments: '/departments',
  'device-names': '/device-names',
  'complaint-categories': '/complaint-categories',
  'system-controls': '/system-controls',
  profile: '/profile',
  preferences: '/preferences',
  help: '/help',
};

const PATH_TO_VIEW = Object.fromEntries(
  Object.entries(VIEW_TO_PATH).map(([view, path]) => [path, view])
);

/**
 * Deep-link allowlist by role (landing prefs + nav-only pages like profile).
 * Mirrors navbar access; technicians do not get bare Devices (remapped below).
 */
const DEEP_LINK_BY_ROLE = {
  technician: [
    'dashboard',
    'assigned-devices',
    'work-logs',
    'maintenance-history',
    'complaints',
    'alerts',
    'analytics',
    'location-transfers',
    'profile',
    'preferences',
    'help',
  ],
  central_help_desk: [
    'dashboard',
    'complaints',
    'alerts',
    'devices',
    'profile',
    'preferences',
    'help',
  ],
  coordinator: [
    'dashboard',
    'devices',
    'complaints',
    'activity-log',
    'alerts',
    'analytics',
    'profile',
    'preferences',
    'help',
  ],
  custodian: [
    'dashboard',
    'devices',
    'complaints',
    'activity-log',
    'alerts',
    'analytics',
    'department-transfers',
    'profile',
    'preferences',
    'help',
  ],
  manager: [
    'dashboard',
    'devices',
    'complaints',
    'activity-log',
    'alerts',
    'analytics',
    'location-transfers',
    'profile',
    'preferences',
    'help',
  ],
  admin: [
    'dashboard',
    'devices',
    'complaints',
    'activity-log',
    'alerts',
    'analytics',
    'approvals',
    'location-transfers',
    'users',
    'settings',
    'audit-viewer',
    'data-import',
    'maintenance-history',
    'profile',
    'preferences',
    'help',
  ],
  superadmin: [
    'dashboard',
    'locations',
    'devices',
    'complaints',
    'alerts',
    'analytics',
    'users',
    'departments',
    'device-names',
    'complaint-categories',
    'data-import',
    'audit-viewer',
    'organization-settings',
    'system-controls',
    'maintenance-history',
    'profile',
    'preferences',
    'help',
  ],
};

export function viewToPath(viewId) {
  if (!viewId) return null;
  return VIEW_TO_PATH[viewId] || null;
}

export function pathToView(pathname) {
  if (!pathname) return null;
  // Normalize trailing slash except root
  const normalized =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;
  return PATH_TO_VIEW[normalized] || null;
}

export function getNavDeepLinkPaths() {
  return Object.values(VIEW_TO_PATH);
}

export function isViewAllowedForRole(viewId, role) {
  if (!viewId || !role) return false;
  const allowed = DEEP_LINK_BY_ROLE[role] || ['dashboard', 'profile', 'preferences', 'help'];
  return allowed.includes(viewId);
}

/**
 * Resolve pathname to a role-allowed view, or null if not a nav deep link / not allowed.
 * Technicians requesting /devices are remapped to assigned-devices (My Devices).
 */
export function resolveDeepLinkView(pathname, role) {
  let view = pathToView(pathname);
  if (!view) return null;

  if (view === 'devices' && role === 'technician') {
    view = 'assigned-devices';
  }

  if (!isViewAllowedForRole(view, role)) {
    return null;
  }
  return view;
}

export function stashPostLoginViewFromPathname(pathname) {
  const view = pathToView(pathname);
  if (view) {
    sessionStorage.setItem(POST_LOGIN_VIEW_KEY, view);
  }
}

export function clearPostLoginView() {
  sessionStorage.removeItem(POST_LOGIN_VIEW_KEY);
}

export function consumePostLoginView() {
  const view = sessionStorage.getItem(POST_LOGIN_VIEW_KEY);
  if (view) {
    sessionStorage.removeItem(POST_LOGIN_VIEW_KEY);
  }
  return view || null;
}

/**
 * Initial view after auth: post-login stash → deep link path → landing preference.
 */
export function resolveInitialAppView({
  pathname,
  role,
  preferredLanding,
  postLoginViewId = null,
}) {
  if (postLoginViewId) {
    let view = postLoginViewId;
    if (view === 'devices' && role === 'technician') {
      view = 'assigned-devices';
    }
    if (isViewAllowedForRole(view, role)) {
      return view;
    }
  }

  const fromPath = resolveDeepLinkView(pathname, role);
  if (fromPath) {
    return fromPath;
  }

  return resolveLandingView(preferredLanding, role) || DEFAULT_LANDING_VIEW;
}
